/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, CircularProgress, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { PermissionItemDto, useUserApi } from "~/api/GeneratedApi";
import { setTitle } from "~/redux/reducers/generalReducer";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { PermissionEdit } from "../../components/PermissionEdit";

interface Params {
  id: string;
}

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const UserEditPage = (): JSX.Element => {
  const { id } = useParams<Params>();
  const userId = parseInt(id, 10);
  const { t } = useTranslation("user-edit");
  const dispatch = useDispatch();
  const classes = useStyles();

  const userApi = useUserApi();

  const { isLoading: isLoadingUserData, data: userData } = useCustomQuery(
    { key: "user", id: userId },
    () => userApi.getUser(userId),
  );

  const {
    isLoading: isLoadingPermissions,
    data: permissions,
    refetch,
  } = useCustomQuery(
    { key: "user-permissions", id: userId },
    () => userApi.getUserPermissions(userId),
    { initialData: [] },
  );

  useEffect(() => {
    if (!userData) return;
    dispatch(
      setTitle([
        {
          label: t("user-management:users"),
          to: "/users",
        },
        { label: userData.name! },
      ]),
    );
  }, [dispatch, userData, t]);

  const { mutateAsync: mutate } = useCustomMutation(
    (mut: PermissionItemDto[]) => userApi.setUserPermissions(userId, mut),
    {
      onSuccess: () => {
        refetch();
      },
    },
  );

  if (isLoadingPermissions || isLoadingUserData) return <CircularProgress />;

  return (
    <Box
      maxWidth={{
        xs: "100%",
        md: "75%",
        lg: "60%",
        xl: "40%",
      }}
      p={3}
    >
      <Typography className={classes.title} variant="h6" gutterBottom>
        {t("title", { user: userData?.name })}
      </Typography>
      <PermissionEdit
        permissions={permissions!}
        onSubmit={(data) => mutate(data.permissions)}
      />
    </Box>
  );
};

export default UserEditPage;
